import * as React from "react";
import { type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  action?: { label: string; onClick?: () => void };
  secondaryAction?: { label: string; onClick?: () => void };
  icon?: LucideIcon;
  children?: React.ReactNode;
}

/**
 * EmptyState states what is needed or missing and offers one clear primary action.
 * Empty is treated as a state, never an error.
 */
export function EmptyState({
  title,
  description,
  action,
  secondaryAction,
  icon: Icon,
  children,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn("flex flex-col items-center gap-3 px-4 py-8 text-center", className)}
      {...props}
    >
      {Icon ? (
        <span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon className="size-5" />
        </span>
      ) : null}
      <div className="flex flex-col gap-1">
        {/* h3, not h2. An empty state sits inside whatever section contains it, so at h2 the
          outline told a screen-reader user that "No packs installed yet" was a sibling of
          "Packs" on /marketplace. It stays a heading so it can still be navigated to. */}
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="max-w-md text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>

      {action || secondaryAction ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action ? (
            <Button size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button size="sm" variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      ) : null}

      {children}
    </div>
  );
}
